import { useIntl } from 'react-intl';

import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { navigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import {
  IconAIProfileSearch,
  IconAICareerChanger,
  IconSalary,
} from '@xing-com/icons';

import { SearchSuggestion } from './search-suggestion';

type BaseProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  subtitle: string;
  kind: FabiJobSearchQueryGenerationUseCase;
  element: string;
  isNew?: boolean;
};
const BaseSuggestion: React.FC<BaseProps> = ({
  Icon,
  title,
  subtitle,
  kind,
  element,
  isNew = false,
}) => {
  const { formatMessage } = useIntl();
  const trackNwt = useJobsNwtTracking();

  const handleOnClick = (): void => {
    trackNwt({
      event: 'opened',
      element,
      elementDetail: 'auto_complete_option',
      componentName: 'ai_search',
      eventSchema: 'aicomponent',
      page: 'jobs_search/serp',
    });

    const params = new URLSearchParams({ [AI_SEARCH_INIT_PROMPT_PARAM]: kind });
    navigate(`${XING_JOBS_SEARCH_KI_PATH}?${params.toString()}`);
  };

  return (
    <SearchSuggestion
      Icon={Icon}
      title={formatMessage({ id: title })}
      subtitle={formatMessage({ id: subtitle })}
      onClick={handleOnClick}
      isNew={isNew}
      condensed
    />
  );
};

const SALARY: React.FC = () => (
  <BaseSuggestion
    Icon={IconSalary}
    title="JOBS_SEARCH_AI_SALARY_SUGGESTION"
    subtitle="JOBS_SEARCH_AI_SALARY_SUGGESTION_SUBTITLE"
    kind={FabiJobSearchQueryGenerationUseCase.EarnMore}
    element="want_to_earn_more"
  />
);
const SWIMP: React.FC = () => (
  <BaseSuggestion
    Icon={IconAIProfileSearch}
    title="JOBS_SEARCH_AI_SWIMP_SUGGESTION"
    subtitle="JOBS_SEARCH_AI_SWIMP_SUGGESTION_SUBTITLE"
    kind={FabiJobSearchQueryGenerationUseCase.SearchWithProfile}
    element="search_with_my_profile"
  />
);
const SWIMS: React.FC = () => (
  <BaseSuggestion
    Icon={IconAICareerChanger}
    title="JOBS_SEARCH_AI_SWIMS_SUGGESTION"
    subtitle="JOBS_SEARCH_AI_SWIMS_SUGGESTION_SUBTITLE"
    kind={FabiJobSearchQueryGenerationUseCase.SearchWithSkills}
    element="search_with_my_skills"
  />
);

type Props = { suggestion: FabiJobSearchQueryGenerationUseCase };
export const Suggestions: React.FC<Props> = ({ suggestion }) => {
  switch (suggestion) {
    case FabiJobSearchQueryGenerationUseCase.SearchWithProfile:
      return <SWIMP />;
    case FabiJobSearchQueryGenerationUseCase.SearchWithSkills:
      return <SWIMS />;
    case FabiJobSearchQueryGenerationUseCase.EarnMore:
      return <SALARY />;
    default:
      return null;
  }
};
